import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, opsFetch } from "./client.js";
import type { OpsRole } from "./me.js";

export interface StaffMember {
  sub: string;
  email: string;
  givenName?: string;
  familyName?: string;
  roles: OpsRole[];
  /** Cognito lifecycle — `FORCE_CHANGE_PASSWORD` until they finish their first sign-in. */
  status: string;
  enabled: boolean;
  createdAt: string;
}

/** What each role actually lets someone do, shown next to the choice. */
export const roleOptions: { value: OpsRole; label: string; blurb: string }[] = [
  { value: "admin", label: "Admin", blurb: "Full management — everything below, plus this page." },
  { value: "support", label: "Support", blurb: "View records and triage; no lifecycle changes." },
  { value: "analyst", label: "Analyst", blurb: "Dashboards only. No record-level customer detail." },
];

export const roleLabels: Record<OpsRole, string> = {
  admin: "Admin",
  support: "Support",
  analyst: "Analyst",
};

export function useStaff(enabled = true) {
  return useQuery({
    queryKey: ["ops", "staff"],
    enabled,
    queryFn: () => opsFetch<{ items: StaffMember[] }>("/ops/staff"),
  });
}

export interface InviteStaffInput {
  email: string;
  role: OpsRole;
  givenName?: string;
  familyName?: string;
}

/** Create the account and send the Cognito invitation (admin only on the server). */
export function useInviteStaff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: InviteStaffInput) =>
      opsFetch<{ member: StaffMember }>("/ops/staff", { method: "POST", json: input }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["ops", "staff"] }),
  });
}

/**
 * Send an invitation again, for someone whose temporary password went stale.
 *
 * Takes only a sub: which address it goes to, and whether the account is even
 * in a state to receive one, are the server's call — read from Cognito rather
 * than passed from here.
 */
export function useResendStaffInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { sub: string }) =>
      opsFetch<{ sub: string; email: string }>(
        `/ops/staff/${encodeURIComponent(input.sub)}/invite`,
        { method: "POST" },
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["ops", "staff"] }),
  });
}

/** Replace a member's role. The server refuses to let an admin change their own. */
export function useSetStaffRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { sub: string; role: OpsRole }) =>
      opsFetch<{ member: StaffMember }>(`/ops/staff/${encodeURIComponent(input.sub)}`, {
        method: "PATCH",
        json: { role: input.role },
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["ops", "staff"] }),
  });
}

/**
 * These routes answer in one envelope (`message`) because the Ops API owns the
 * write outright — there is no upstream system whose validation shape could
 * also come through. Surface it verbatim: "That email already has a staff
 * account" is the whole answer an admin needs.
 */
export function staffErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { message?: string } | null;
    return body?.message ?? err.message;
  }
  return err instanceof Error ? err.message : "Something went wrong.";
}
